import { Module, PATH_DICTS } from "@/constants";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { FilterType } from "@/lib/filters";
import { StoreGroupService } from "@/modules/storeGroups/service";
import { ReadStoreGroupMember } from "@/types/storeGroupMember";
import { ComponentProps, forwardRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import AsyncSelect, { Option, type AsyncSelectBaseProps } from "./AsyncSelect";
import appConfiguration from "./cfg/AppCfg";
import { useCreateDataTableOptions } from "./data-table/hooks/useCreateDataTableOptions";
import { EntityListLink } from "./EntityListLink";

interface StoreGroupSelectProps extends AsyncSelectBaseProps {
  className?: string;
  inDialog?: boolean;
  clear?: boolean;
  showLink?: boolean;
  defaultItem?: Partial<Option>;
}

export default forwardRef<HTMLButtonElement, StoreGroupSelectProps>(
  function StoreGroupSelect(
    { value, inDialog, showLink, defaultItem, ...props },
    ref,
  ) {
    const { t } = useTranslation();
    const name = "storeGroupSelect";
    const options = useCreateDataTableOptions(name);
    const queryKey = useDynamicQueryParams<ReadStoreGroupMember>(name, options);

    const onFilter: ComponentProps<typeof AsyncSelect>["onChange"] =
      useCallback(
        (filterValue) => {
          options.setFilterValue("name", {
            value: filterValue,
            filter: FilterType.IncludesString,
          });
        },
        [options],
      );

    return (
      <span className="flex items-center gap-1">
        <AsyncSelect
          queryKey={queryKey}
          queryFn={() =>
            StoreGroupService.getList({
              filters: options.filters,
              limit: appConfiguration.selectComponentsPaginationLimit,
            })
          }
          placeholder={t("storeGroupSelect.placeholder")}
          loadingLabel={t("storeGroupSelect.loadingStoreGroups")}
          noResultsLabel={t("storeGroupSelect.noResults")}
          searchLabel={t("storeGroupSelect.search")}
          enableFilter
          {...props}
          onFilter={onFilter}
          value={value ? value.toString() : undefined}
          ref={ref}
          inDialog={inDialog}
          defaultItem={defaultItem}
        />
        {showLink && (
          <EntityListLink
            path={`${PATH_DICTS}/${Module.StoreGroups}`}
            label={t("storeGroupSelect.link")}
          />
        )}
      </span>
    );
  },
);
